import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { fetchUserAttributes } from 'aws-amplify/auth'
import { fetchDashboardData, fetchDashboardInsights } from '@/api/dashboardApi'
import { getCurrentUserDetailsRequest } from '@/api/sessionApi'
import { isApiSuccess, getApiMessage } from '@/mixins/Extras/ApiResponse'
import { unwrapApiList, unwrapApiPayload } from '@/utils/graphql'
import { normalizeSessionPayload } from '@/utils/session'
import {
  currentDashboardPeriod,
  formatDashboardPeriod,
} from '@/utils/format'

function buildPeriodOptions(count = 6) {
  const options = []
  const now = new Date()
  for (let i = 0; i < count; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const value = `${mm}${d.getFullYear()}`
    options.push({ value, label: formatDashboardPeriod(value) })
  }
  return options
}

function normalizeDashboardRow(raw) {
  if (!raw || typeof raw !== 'object') return null
  const daily = raw.daily_breakdown
  return {
    ...raw,
    daily_breakdown: Array.isArray(daily) ? daily : [],
  }
}

function resolveDashboardRow(response) {
  const fromPayload = unwrapApiPayload(response)
  if (
    fromPayload &&
    typeof fromPayload === 'object' &&
    'total_calls' in fromPayload
  ) {
    return normalizeDashboardRow(fromPayload)
  }
  const fromList = unwrapApiList(response)[0]
  return normalizeDashboardRow(fromList)
}

export function useDashboardData() {
  const store = useStore()
  const route = useRoute()

  const dashboard = ref(null)
  const insights = ref(null)
  const refreshing = ref(false)
  const loadAttempted = ref(false)
  const selectedPeriod = ref(currentDashboardPeriod())
  const periodOptions = ref(buildPeriodOptions())
  const chartPeriod = ref('30d')
  const chartMetric = ref('calls')

  const isSessionReady = computed(() => store.getters.isSessionReady)
  const sessionContext = computed(() => store.getters.sessionContext)
  const organization = computed(() => store.state.organization)
  const userName = computed(() => store.state.userName)
  const userEmail = computed(() => store.state.userEmail)

  const showSkeleton = computed(() => {
    if (dashboard.value) return false
    if (isSessionReady.value && loadAttempted.value && !refreshing.value) {
      return false
    }
    return !isSessionReady.value || refreshing.value || !loadAttempted.value
  })

  const dailyRows = computed(() => {
    const rows = dashboard.value?.daily_breakdown ?? []
    return [...rows].sort((a, b) =>
      String(b?.date ?? '').localeCompare(String(a?.date ?? '')),
    )
  })

  const periodLabel = computed(() =>
    formatDashboardPeriod(
      dashboard.value?.dashboard_period ?? selectedPeriod.value,
    ),
  )

  function buildDashboardInput() {
    const { org_id } = sessionContext.value ?? {}
    return {
      org_id,
      dashboard_period: selectedPeriod.value,
    }
  }

  async function refreshCurrentUser() {
    const raw = await getCurrentUserDetailsRequest()
    if (!isApiSuccess(raw)) {
      throw new Error(getApiMessage(raw, 'Failed to refresh organisation details'))
    }

    const session = normalizeSessionPayload(raw)
    if (!session?.currentUserId || !session?.orgId) {
      throw new Error('Account session is missing organisation context.')
    }

    let email = session.userEmail || userEmail.value?.trim() || ''
    if (!email) {
      const attrs = await fetchUserAttributes()
      email = attrs?.email?.trim() || ''
    }
    if (!email) {
      throw new Error('No email found on your account.')
    }

    store.commit('SET_SESSION', {
      currentUserId: session.currentUserId,
      orgId: session.orgId,
      userEmail: email,
      emailAddress: session.emailAddress,
      emailChannel: session.emailChannel,
      userName: session.userName,
      userRole: session.userRole,
      organization: session.organization,
    })
  }

  async function fetchDashboard() {
    const { org_id } = sessionContext.value ?? {}
    if (!isSessionReady.value || !org_id) return null

    const response = await fetchDashboardData(buildDashboardInput())
    if (!isApiSuccess(response)) {
      dashboard.value = null
      insights.value = null
      return response
    }

    const row = resolveDashboardRow(response)
    dashboard.value = row
    if (row?.dashboard_period && row.dashboard_period !== selectedPeriod.value) {
      selectedPeriod.value = row.dashboard_period
    }

    const insightsResponse = await fetchDashboardInsights(buildDashboardInput())
    insights.value = isApiSuccess(insightsResponse) ? insightsResponse : null

    return { ok: true }
  }

  async function refreshDashboard() {
    if (!isSessionReady.value) return { ok: false }

    refreshing.value = true
    loadAttempted.value = true
    try {
      await refreshCurrentUser()
      const result = await fetchDashboard()
      return result
    } catch (err) {
      dashboard.value = null
      throw err
    } finally {
      refreshing.value = false
    }
  }

  function onPeriodChange() {
    if (isSessionReady.value && !refreshing.value) {
      fetchDashboard()
    }
  }

  watch(
    isSessionReady,
    (ready) => {
      if (ready) refreshDashboard()
    },
    { immediate: true },
  )

  watch(
    () => route.name,
    (name) => {
      if (name === 'Dashboard' && isSessionReady.value) {
        refreshDashboard()
      }
    },
  )

  return {
    dashboard,
    insights,
    refreshing,
    loadAttempted,
    selectedPeriod,
    periodOptions,
    chartPeriod,
    chartMetric,
    isSessionReady,
    organization,
    userName,
    showSkeleton,
    dailyRows,
    periodLabel,
    refreshDashboard,
    fetchDashboard,
    refreshCurrentUser,
    onPeriodChange,
  }
}
